import type { OptionsMap } from '@openx/types/options.ts';
import type { RetargetingFormState, RetargetingSegmentApiParams } from '@openx/types/targeting/retargeting.ts';
import type { TargetingFieldsValues, TargetingReadOnlyProps } from '@openx/types/targeting/targeting.ts';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useTargetingDisabledState } from '../../utils/hooks/useTargetingDisabledState.ts';
import { useDropdownOpen } from '../shared/ReadOnlyTargetingDropdown/hooks/useDropdownOpen.ts';
import { StyledReadOnlyTargetingDropdown } from '../shared/ReadOnlyTargetingDropdown/ReadOnlyTargetingDropdown.ts';
import { TargetingReadOnly } from '../shared/TargetingReadOnly/TargetingReadOnly.tsx';
import { FormPreviewItems } from './FormPreviewItems.tsx';
import { Items } from './Items.tsx';
import { fromRetargetingSegmentApiParams } from './utils/retargetingFormState.ts';

type RetargetingReadOnlyProps = Omit<TargetingReadOnlyProps<RetargetingSegmentApiParams>, 'field'> & {
  field: TargetingFieldsValues;
  userChangedInitialValue?: boolean;
  formState: RetargetingFormState;
  onClear: () => void;
  isSegmentsLoading?: boolean;
  segmentOptions?: OptionsMap;
};

export const RetargetingReadOnly = ({
  name,
  field,
  userChangedInitialValue,
  onOpenDrawerClick,
  onRemoveClick,
  targetingParams,
  readonly,
  formState,
  onClear,
  isSegmentsLoading = false,
  segmentOptions,
}: RetargetingReadOnlyProps) => {
  const { isDisabledOrHasError, disabledReasonMessage, errorReasonMessage } = useTargetingDisabledState(field);
  const { t } = useTranslation();

  const isEmpty = !targetingParams?.val;
  const optionsCount = isEmpty ? 0 : 1;
  const { isOpen, toggleOpen } = useDropdownOpen(!isEmpty);

  const previewState = useMemo(
    () => (targetingParams ? fromRetargetingSegmentApiParams(targetingParams) : formState),
    [formState, targetingParams]
  );

  if (!readonly) {
    return (
      <TargetingReadOnly
        name={name}
        field={field}
        userChangedInitialValue={userChangedInitialValue}
        isEmpty={isEmpty}
        onOpenDrawerClick={onOpenDrawerClick}
        onRemove={onRemoveClick}
        readonly={readonly}
        isDisabled={isDisabledOrHasError}
        disabledReasonMessage={disabledReasonMessage}
        description={t(
          'Attach one eligible retargeting segment to this delivery object. Choose whether to retarget or suppress the selected audience.'
        )}
        isSingleChoice
        optionsCount={optionsCount}
        isOpen={isOpen}
        toggleOpen={toggleOpen}
      >
        {!isEmpty && isOpen && (
          <StyledReadOnlyTargetingDropdown>
            <FormPreviewItems
              targetingParams={previewState}
              readonly={readonly}
              onClear={onClear}
              onOpenDrawerClick={onOpenDrawerClick}
              onRemove={onRemoveClick}
              isDisabled={isDisabledOrHasError}
              optionsCount={optionsCount}
              disabledReasonMessage={errorReasonMessage}
              isLoading={isSegmentsLoading}
              options={segmentOptions}
            />
          </StyledReadOnlyTargetingDropdown>
        )}
      </TargetingReadOnly>
    );
  }

  return (
    <TargetingReadOnly
      name={name}
      field={field}
      userChangedInitialValue={userChangedInitialValue}
      isEmpty={isEmpty}
      onOpenDrawerClick={onOpenDrawerClick}
      onRemove={onRemoveClick}
      readonly={readonly}
      isDisabled={isDisabledOrHasError}
      disabledReasonMessage={disabledReasonMessage}
      description={t(
        'Attach one eligible retargeting segment to this delivery object. Choose whether to retarget or suppress the selected audience.'
      )}
    >
      {!isEmpty && (
        <Items
          targetingParams={previewState}
          readonly={readonly}
          onClear={onClear}
          isLoading={isSegmentsLoading}
          options={segmentOptions}
        />
      )}
    </TargetingReadOnly>
  );
};
